import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
// Context
import { useFiltersContext } from 'components/common2/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_MENTIONS_STORES } from 'api/store/queries';
// Types
import { StoreStatus, StoresOrderBy, UserRole } from 'api/graphql-global-types';
import {
  GetMentionsStores,
  GetMentionsStoresVariables,
} from 'api/store/types/GetMentionsStores';
// Ui3
import { Mention } from 'ui3/Mentions/Mentions';
import Input from 'ui3/Input/Input';
// Styles
import styles from './MillionsMentionsSelect.module.scss';

type MillionsMentionsProps = {
  mentions: Mention[];
  onChange: (mentions: Mention[]) => void;
  handleMillionsMentionRemove: (index: number) => void;
};

import { TargetType } from 'api/graphql-global-types';

const reformattedMention = (option) =>
  ({
    store: {
      storeDetails: {
        storeId: option.value,
        storeName: option.label,
        avatarURL: option.avatarURL,
      },
    },
    mentionType: 'MEDIA_POST',
    targetType: TargetType.REGISTERED,
    unregisteredContact: null,
  } as any);

export const MillionsMentionsSelect = ({
  mentions,
  onChange,
  handleMillionsMentionRemove,
}: MillionsMentionsProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { searchKey, search } = useFiltersContext();

  const inputVariables = useMemo(
    () => ({
      offset: 0,
      limit: 20,
      ...(searchKey &&
        search[searchKey] && {
          [searchKey]: search[searchKey],
        }),
    }),
    [searchKey, search]
  );

  const { data: getStoresData, refetch } = useQuery<
    GetMentionsStores,
    GetMentionsStoresVariables
  >(GET_MENTIONS_STORES, {
    variables: {
      storeRoles: [
        UserRole.Athlete,
        UserRole.Organization,
        UserRole.ContentCreator,
      ],
      input: {
        ...inputVariables,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const storesData = getStoresData?.stores?.entities || [];

  const formattedStoresData = storesData.map((item) => ({
    value: item?.storeDetails?.storeId,
    label: item?.storeDetails?.storeName || '',
    avatarURL: item?.storeDetails?.avatarURL || '',
  }));

  // Filter out already selected stores from dropdown
  const selectedStoreIds = useMemo(
    () =>
      new Set(
        mentions
          .map((m) => (m as any)?.store?.storeDetails?.storeId)
          .filter((id) => id)
      ),
    [mentions]
  );

  const availableStores = formattedStoresData.filter(
    (store) => !selectedStoreIds.has(store.value)
  );

  const handleOnSearchStores = useMemo(() => {
    const loadOptions = (storeName: string) => {
      if (storeName && storeName.trim().length > 0) {
        refetch({
          storeRoles: [
            UserRole.Athlete,
            UserRole.Organization,
            UserRole.ContentCreator,
          ],
          input: {
            storeName,
            orderBy: StoresOrderBy.storeName,
            status: [StoreStatus.Active],
            limit: 20,
          },
        });
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    };

    return debounce(loadOptions, 400);
  }, [refetch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    handleOnSearchStores(value);
  };

  const handleStoreSelect = (store) => {
    const newMention = reformattedMention(store);
    onChange([...mentions, newMention]);
    setSearchQuery('');
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemove = (index: number) => {
    handleMillionsMentionRemove(index);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.searchContainer}>
        <Input
          ref={inputRef}
          name="storeSearch"
          placeholder="Search"
          icon="search"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            if (searchQuery && availableStores.length > 0) {
              setShowDropdown(true);
            }
          }}
        />
        {showDropdown && availableStores.length > 0 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            {availableStores.map((store) => (
              <div
                key={store.value}
                className={styles.dropdownOption}
                role="button"
                tabIndex={0}
                onClick={() => handleStoreSelect(store)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStoreSelect(store);
                  }
                }}
              >
                <img
                  src={store.avatarURL || '/favicon.png'}
                  alt={store.label || 'Store avatar'}
                  className={styles.optionAvatar}
                />
                <span className={styles.optionLabel}>{store.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {mentions.length > 0 && (
        <div className={styles.selectedStoresContainer}>
          {mentions.map((mention, index) => {
            const storeName =
              (mention as any)?.store?.storeDetails?.storeName || '';
            const avatarURL =
              (mention as any)?.store?.storeDetails?.avatarURL ||
              '/favicon.png';
            const storeId = (mention as any)?.store?.storeDetails?.storeId;

            return (
              <div
                key={`${storeId || index}-${index}`}
                className={styles.mentionCard}
              >
                <img
                  src={avatarURL || '/favicon.png'}
                  alt={storeName || 'Store avatar'}
                  className={styles.avatar}
                />
                <span className={styles.mentionName}>{storeName}</span>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleRemove(index)}
                  type="button"
                  aria-label="Delete mention"
                >
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MillionsMentionsSelect;
